<template>
  <div class="container-cap" id="establishments-data" v-if="surveillance">
    <div class="row">
      <div class="col-xs-12">
        <h2>
          Indicateurs nationaux, taux d'incidence et caractéristiques des infections
          <div v-if="dataset != null">
            <p>
              <small>
                Pour les infections issues des Données «{{ dataset }}»
              </small>
            </p>
            <dropdown tag="span">
              <btn type="primary" class="dropdown-toggle" role="button"
                   size="sm"
                   id="finess-list">
                Changer &nbsp;&nbsp;
                <span class="caret"></span>
              </btn>
              <template slot="dropdown">
                <li v-if="datasets.spiadi">
                  <a role="button"
                     @click="setDataset(datasets.spiadi)"
                     :disabled="dataset === datasets.spiadi.name">
                    {{ datasets.spiadi.name }}
                  </a>
                </li>
                <li v-for="(c, k) in datasets.others"
                    :key="`change-carac-${k}`">
                  <a role="button"
                     @click="setDataset(c)"
                     :disabled="dataset === c.name">
                    {{ c.name }}
                  </a>
                </li>
              </template>
            </dropdown>
          </div>
        </h2>
        <hr>
        <div v-if="!hasAccess">
          <div class="col-lg-9 col-xs-12">
            <alert type="warning" class="text-justify">
              <h4>
                Accès non autorisé aux résultats de l'établissement
                «{{ surveillance.establishment.name }}»
              </h4>
              <p>
                Pour des raisons de confidentialité (CNIL), les résultats
                {{ surveillance.year }} sont, par défaut, uniquement accessibles
                au responsable du programme.
              </p>
              <p>
                <strong>
                  Pour obtenir vos résultats, merci de bien vouloir contacter le
                  responsable du programme (personne ayant effectué
                  l'inscription) pour votre établissement :
                  {{ surveillance.registrator.firstName }} {{ surveillance.registrator.name }}.
                </strong>
              </p>
              <p>
                N'hésitez pas à revenir vers nous via le
                <router-link to="/contact">formulaire de contact</router-link> si
                vous souhaitez avoir plus de précisions.
              </p>
            </alert>
          </div>
        </div>
        <div v-else-if="dataset == null">
          <div v-if="year === '2027'">
            <alert type="warning" class="col-lg-9">
              <p class="text-justify">
                <strong>
                  Le module résultat est en cours d'adaptation aux protocoles {{year}}.
                </strong>
              </p>
            </alert>
          </div>
          <div v-else>
            <alert type="warning">
              <p class="text-justify">
                <strong>
                  Merci de sélectionner les données pour lesquelles vous
                  souhaitez obtenir vos résultats.
                </strong>
              </p>
            </alert>
            <div class="row">
              <div class="col-xs-12">
                <h3>Vos données envoyées au programme national</h3>
                <div class="row">
                  <div class="col-xs-6 col-md-4 col-lg-3 open-button-container">
                    <btn class="open-button btn-results" block
                         @click="setDataset(datasets.spiadi)">
                      <strong>{{ datasets.spiadi.name }}</strong>
                    </btn>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="datasets.others.length > 0" class="row">
              <div class="col-xs-12">
                <h3>Vos données personnalisées</h3>
                <div class="row" >
                  <div v-for="(c, k) in datasets.others"
                       class="col-xs-6 col-md-4 col-lg-3 open-button-container"
                       :key="`nav-carac-${k}`">
                    <btn class="open-button btn-results" block
                         @click="setDataset(c)">
                      <strong>{{ c.name }}</strong>
                    </btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <alert type="warning">
            <h4>
              Bienvenue sur la page Résultats «{{dataset}}» pour l'établissement
              «{{surveillance.establishment.name}}»
            </h4>
            <p>
              <strong>
                Cette page est construite à partir des Données «{{dataset}}» de
                toutes les modalités validées de la surveillance.
              </strong>
              <btn size="xs" type="default" @click="helpCollapse = !helpCollapse">
                En savoir plus
              </btn>
              </p>
              <p>
                <collapse v-model="helpCollapse">
                  <p>
                    <strong>Une fois par jour</strong>, ces données nous permettent
                    d'extraire :
                    <ul>
                      <li>toutes les bactériémies acquises dans l'établissement, et</li>
                      <li>toutes les données d'activité pour la période surveillée.</li>
                    </ul>
                  </p>

                  <p>
                    Vous trouverez ci-dessous différentes présentations des statistiques
                    calculées à partir de ces extractions :
                    <ul>
                      <li>
                        les tableaux de taux d'incidence <strong>provisoires</strong>
                        pour les admissions et les journées d'hospitalisation,
                      </li>
                      <li>
                        les graphiques vous permettant de comparer vos taux d'incidence
                        avec ceux obtenus par les établissement de même type, et
                      </li>
                      <li>
                        les caractéristiques des infections extraites.
                      </li>
                    </ul>
                  </p>
                </collapse>
              </p>
            <p>
              <btn type="primary" @click="help = true">
                Comment lire les résultats
                <span class="glyphicon glyphicon-question-sign"
                      style="margin-left: 0.3rem"
                      aria-hidden="true"></span>
              </btn>
            </p>
          </alert>
          <modal v-model="help"
           :footer="false"
           size="lg"
           title="Informations sur les résultats">
            <how-to-read-results></how-to-read-results>
          </modal>
          <alert type="warning" class="text-justify" v-if="hasResultsAndModule">
            <h4>
              Accès au résultats
            </h4>
            <div class="row">
              <div class="col-xs-12">
                <p>
                  <strong>
                    Les droits d'accès aux résultats (définis par le responsable
                    du programme) vous permettent d'accéder à la fois aux taux
                    d'incidence de tout l'établissment et aux résultats complets
                    pour les modalités
                    <span v-for="m in visibleResultsModules"
                          :class="`label label-${m}`"
                          :key="`nav-${m}`">
                      {{ surveillanceModulesInfo[m].label }}
                    </span>
                  </strong>
                </p>
                <p>
                  Vous pouvez à tout moment basculer d'une vue à l'autre en
                  utilisant les boutons ci dessous.
                </p>
              </div>
            </div>
          </alert>
          <div class="row" v-if="hasResultsAndModule">
            <div class="col-xs-6">
              <div class="panel panel-default">
                <div class="panel-heading">
                  <div class="panel-title">
                    <h4 class="text-center">
                      Établissement complet
                    </h4>
                  </div>
                </div>
                <div class="panel-body">
                  <p>
                    Cette vue comprend :
                    <ul>
                      <li>
                        Les taux d'incidences de tout l'établissement
                      </li>
                      <li>
                        Les graphiques des populations nationales
                      </li>
                    </ul>
                  </p>
                </div>
                <div class="panel-footer">
                  <btn type="primary" @click="etbResults = true" block>
                    Voir les résultats de l'établissement
                  </btn>
                </div>
              </div>
            </div>
            <div class="col-xs-6">
              <div class="panel panel-default">
                <div class="panel-heading">
                  <div class="panel-title">
                    <h4 class="text-center">
                      Résultats <span v-for="m in visibleResultsModules"
                                      :class="`label label-${m}`"
                                      :key="`panel-nav-${m}`">
                      {{ surveillanceModulesInfo[m].label }}
                      </span>
                    </h4>
                  </div>
                </div>
                <div class="panel-body">
                  <p>
                    Cette vue comprend :
                    <ul>
                      <li>
                        Les taux d'incidences partiels de l'établissement,
                        calculés à partir des modalités autorisées
                      </li>
                      <li>
                        Les graphiques des populations nationales
                      </li>
                      <li>
                        Les caractéristiques des infections extraites à partir
                        des modalités autorisées
                      </li>
                    </ul>
                  </p>
                </div>
                <div class="panel-footer">
                  <btn type="primary" @click="etbResults = false" block>
                    Voir les résultats des modalités
                  </btn>
                </div>
              </div>
            </div>
          </div>
          <hr/>
          <div v-if="!(hasResultsAndModule && etbResults == null)">
            <div v-if="hasResultsAndModule">
              <h3 v-if="etbResults">
                Résultats de l'établissement
              </h3>
              <h3 v-else>
                Résultats
                <span v-for="m in visibleResultsModules"
                      :class="`label label-${m}`"
                      :key="`title-${m}`">
                  {{ surveillanceModulesInfo[m].label }}
                </span>
              </h3>
            </div>
            <div class="row" v-if="!display">
              <div class="col-xs-12">
                <div v-if="canGetResults && !isRegistrator">
                  <alert type="warning">
                    <p>
                      <strong>
                        La participation de votre établissement a la surveillance
                        a été validée par le responsable du programme.
                      </strong>
                    </p>
                    <p>
                      Les résultats de votre établissement hors données de santé seront
                      accessibles après la journée nationale {{ year }}.
                    </p>
                  </alert>
                </div>
                <div v-else>
                  <strong><loading-text>Chargement</loading-text></strong>
                </div>
              </div>
            </div>
            <div class="row" v-else>
              <div class="col-xs-12">
                <div v-if="isUpToDateResult">
                  <div v-if="showFilters && infections != null && infections.length > 0">
                    <sui-data-filter-input v-model="filteredInfections"
                                           :list="infections"
                                           :sticky="false"
                                           :filterSearch="filters"
                                           :variables="filterVariables">
                      <p>
                        Le champ texte ci-dessous vous permet de filtrer les infections
                        prises en compte dans le calcul des statistiques. Les taux
                        d'incidence et les caractéristiques seront immédiatement
                        recalculés en tenant compte du filtre choisi, vous permettant
                        d'explorer les données en profondeur.
                      </p>
                    </sui-data-filter-input>
                  </div>

                  <sui-results-dashboard :results="flatResults"
                                        :otherTypeResults="otherTypeResults"
                                        :key="`result-dashboard-${stickyOffset}`"
                                        :year="parseInt(year)"
                                        :chartYear="chartYear"
                                        :referenceDataset="referenceDataset"
                                        show-values-toggle
                                        :chartTitle="`${dataset} - ${surveillance.establishment.name}`"
                                        :caracteristics="flatDistribution"
                                        :stickyOffset="stickyOffset"
                                        :pdfOptions="pdfOptions"
                                        :listFilter="filteredInfections.filter">
                  </sui-results-dashboard>
                </div>
                <div v-else-if="flatResults == null">
                  <alert type="warning">
                    <p>
                      <strong>
                        Sur cette page apparaîtront les résultats «{{dataset}}» dès
                        lors qu'une modalité de surveillance aura été validée.
                      </strong>
                    </p>
                    <p>
                      Si vous pensez avoir validé une modalité, vérifiez dans l'encart
                      «{{dataset}}» correspondant que :
                      <ul>
                        <li>la période surveillée a été choisie,</li>
                        <li>les fiches ont été validées, et</li>
                        <li>les données d'activité ont bien été enregistrées.</li>
                      </ul>
                    </p>
                    <p>
                      <strong>
                        Vérifiez également que votre fiche établissement est
                        remplie et validée.
                      </strong>
                    </p>
                  </alert>
                </div>
                <div v-else>
                  <div v-if="datasetUpdateDate">
                    <alert type="warning">
                      <h4 class="text-justify">
                        <span class="glyphicon glyphicon-warning-sign"
                              style="margin-left: 0.3rem"></span>
                        Attention ! Vos modifications sur les données n'ont
                        pas encore été prises en compte dans le calcul des résultats.<br>
                      </h4>
                      <p>
                        <strong>
                          Il vous faut attendre le lendemain afin que vos modifications
                          soient répercutées sur la page Résultats.
                        </strong>
                      </p>
                      <p>
                        Dernières modifications sur les données :
                        <strong>{{ formatDate(datasetUpdateDate) }}</strong>
                      </p>
                    </alert>
                  </div>
                  <div v-else>
                    <alert type="info">
                      <p class="text-justify">
                        <strong>
                          Si vous avez modifié un jeu de données,
                          il vous faut attendre le lendemain pour que les
                          données soient répercutées sur la page résultat.
                        </strong>
                      </p>
                      <p>
                        Date de la dernière extraction :
                        <strong>{{ formatDate(lastExtractionDate) }}</strong>
                      </p>
                    </alert>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
import assert from '@/sui/tools/assert'
import ice from '@/sui/tools/ice'
import moment from 'moment'
import loading from '@/sui/components/loadingtext'
import { mapState, mapGetters } from 'vuex'
import go from '@/gojs/go'
import results from '@/sui/data/results'
import distribution from '@/sui/data/distribution'
import caracteristics from '@/sui/data/caracteristics'
import modules from '@/sui/data/modules'
import suiResultsDashboard from '@/sui/components/results/dashboard'
import suiResultsCaracteristics from '@/sui/components/results/caracteristics'
import suiDataFilterInput from '@/sui/components/dataFilterInput'
import sc from '@/sui/sc/caracteristics'
import readResults from '@/sui/components/results/readResults'

export default {
  name: 'establishment-data',

  props: {
    surveillance: {
      type: Object
    }
  },

  data: function () {
    return {
      filters: [
        {
          title: 'Provenance',
          list: [
            {
              name: 'Infection acquise en réanimation',
              filter: '$spe ~ "REA"'
            },
            {
              name: `Infection acquise dans l'uf 1234`,
              filter: '$uf ~ "1234"'
            }
          ]
        },
        {
          title: 'Patient',
          list: [
            {
              name: 'Cas covid ou cas covid probable',
              filter: '$covid < 3'
            },
            {
              name: 'Décédé',
              filter: '$décès = 1'
            }
          ]
        },
        {
          title: 'Infection',
          list: [
            {
              name: 'Infection associée à un dispositif intra vasculaire',
              filter: '$pe ~ "^(C|D1)"'
            },
            {
              name: `Infection acquise dans l'établissement`,
              filter: '$acquise < 3'
            }
          ]
        },
        {
          title: 'Micro-organisme',
          list: [
            {
              name: 'Infection avec un staphylocoque doré',
              filter: '$mo : "STA AUR"'
            },
            {
              name: 'Infection avec un staphylocoque',
              filter: '$mo ~ "STA"'
            }

          ]
        }
      ],
      helpCollapse: false,
      filterHelp: false,
      selection: null,
      text: '',
      pdfOptions: {
        legend: true,
        showUfs: true,
        showMean: true
      },
      filter: '',
      filteredInfections: { filter: '', list: [] },
      referenceFilteredInfections: { filter: '', list: [] },
      sc: sc,
      dsall: null,
      results: null,
      restricted: true,
      help: false,
      etbResults: null,

      dataset: null,
      datasetUpdateDate: null
    }
  },

  computed: {
    ...mapState({
      all: state => state.infections
    }),

    ...mapGetters({
      getResult: 'results/getResult',
      userId: 'user/id'
    }),

    filterVariables: function () {
      return sc.filterVariables(this.year)
    },

    showFilters: function () {
      return (this.isRegistrator || (this.hasModule && !this.etbResults)) &&
             this.infections != null && this.infections.length > 0 &&
             (!this.spiadi || (this.results != null && !this.results.empty))
    },

    stickyOffset: function () {
      return 0
    },

    lastExtractionDate: function () {
      const infections = this.infections
      if (infections == null) {
        return null
      }

      const dateByModality = {}
      for (let i = 0; i < infections.length; i++) {
        const infection = infections[i]
        if (dateByModality[infection.prot] != null) {
          continue
        }
        dateByModality[infection.prot] = infection.extr.date
      }

      let result = moment().startOf('day').add(5, 'hours')
      for (let key in dateByModality) {
        const date = moment(dateByModality[key])
        if (date.isAfter(result)) {
          result = date
        }
      }

      return result
    },

    isUpToDateResult: function () {
      if (this.flatResults == null) {
        return false
      }

      const extraction = this.lastExtractionDate
      const update = this.datasetUpdateDate
      return extraction == null || update == null || extraction.isAfter(update)
    },

    display: function () {
      const ns = this.surveillance != null
      const ni = this.infections != null && this.dsall != null
      const nr = this.results != null || !this.spiadi
      if (this.isRegistrator || this.hasModule) {
        return ns && ni && nr
      }

      if (this.hasSpiadiResults && !this.hasModule) {
        return ns && nr
      }

      return false
    },

    year: function () {
      return this.$route.params.year
    },

    infections: function () {
      const id = this.surveillance.id
      const t = (l) => l.available && l.id === id
      if (!t(this.all.bact) || !t(this.all.pneu)) {
        return null
      }

      const list = this.all.bact.all.slice()
      list.push(...this.all.pneu.all)
      return ice.freeze(list)
    },

    referenceInfections: function () {
      if (this.referenceFilteredInfections.filter === '') {
        return this.infections
      }

      return this.referenceFilteredInfections.list
    },

    incidences: function () {
      if (this.infections == null ||
          this.dsall == null ||
          this.dsall.length === 0) {
        return null
      }

      const data = {
        infections: [],
        estbInfections: {
          bact: [],
          pneu: []
        }

      }
      this.referenceInfections.forEach(
        i => {
          if (data.estb == null) {
            data.estb = i.estb
          }
          data.infections.push(i)
          const t = i.itb != null ? 'pneu' : 'bact'
          data.estbInfections[t].push(i)
        }
      )

      let t = null
      let dist = null
      try {
        const c = go.incidences.countBact(data.estbInfections.bact)
        c.push(...go.incidences.countPneu(data.estbInfections.pneu))

        t = go.incidences.compute(c, this.dsall)
        dist = distribution('bact', data.infections, this.year)
      } catch (e) {
        this.$notify({
          type: 'danger',
          title: 'Erreur !',
          duration: 0,
          content: `Une erreur est survenue pendant le calcul des
incidences et des caractéristiques : ${e}.`
        })
      }

      data.incidences = t
      data.distribution = dist
      return ice.freeze(data)
    },

    isRegistrator: function () {
      if (this.surveillance == null) {
        return false
      }

      return this.surveillance.isRegistrator(this.userId)
    },

    canGetResults: function () {
      if (this.surveillance == null) {
        return false
      }

      const result = this.surveillance.canGetStatistics(this.userId, 'results')
      return result
    },

    hasSpiadiResults: function () {
      return this.spiadi && this.canGetResults
    },

    visibleResultsModules: function () {
      if (this.surveillance == null) {
        return null
      }
      const mods = []

      const moduleList = this.surveillance.availableSurveillanceModules
      for (let i = 0; i !== moduleList.length; ++i) {
        const m = moduleList[i]
        if (m === 'results') {
          continue
        }
        if (this.surveillance.canGetStatistics(this.userId, m)) {
          mods.push(m)
        }
      }
      return mods
    },

    hasModule: function () {
      return this.visibleResultsModules != null &&
             this.visibleResultsModules.length > 0
    },

    hasAccess: function () {
      return this.isRegistrator || this.canGetResults || this.hasModule
    },

    hasResultsAndModule: function () {
      return !this.isRegistrator && this.canGetResults && this.hasModule
    },

    surveillanceModulesInfo: function () {
      return modules.get(
        this.surveillance.year, (m) => m.sheets && m.type === modules.types.surveillance
      )
    },

    otherTypeResults: function () {
      return this.hasSpiadiResults && this.filteredInfections.filter === '' && !this.results.empty
        ? {
          noQuality: true,
          values: {
            'TOUS': this.results
          }
        } : null
    },

    flatResults: function () {
      if (this.hasSpiadiResults && this.referenceFilteredInfections.filter === '') {
        const srv = this.results
        if (srv.empty) {
          return null
        }

        return srv
      }

      if (this.incidences == null) {
        return null
      }

      const type = results.getEstablishementType(
        this.surveillance.establishment.type
      )

      const result = results.flattenIncidences(
        true, this.incidences.incidences, type, false
      )

      if (this.filteredInfections.filter !== '') {
        return result
      }

      if (this.spiadi) {
        for (let k in result.values) {
          let s = this.results.values[k]
          if (s != null) {
            const b = this.compareResults(result.values[k], s)
            if (!b) {
              result.values[k].quality = 5
              result.hasPartial = true
            } else {
              result.values[k] = s
            }
          }
        }
      }

      return result
    },

    chartYear: function () {
      const y = parseInt(this.year)
      return y === 2027 ? 2026 : y
    },

    referenceDataset: function () {
      return `SPIADI${this.chartYear}`
    },

    flatDistribution: function () {
      if (this.incidences == null || this.etbResults) {
        return null
      }
      return caracteristics.flatten.infections(this.incidences.distribution)
    },

    datasets: function () {
      const spiadiNameDset = `SPIADI${this.year}`
      const result = {
        spiadi: { name: spiadiNameDset, lastSave: null },
        others: []
      }
      if (this.surveillance == null) {
        return result
      }

      const mods = this.surveillance.availableSurveillanceModules
      const lastUpdateByName = {}
      for (let i in mods) {
        const m = mods[i]
        const mc = this.surveillance.modules[m]
        if (mc.dset == null ||
            !this.surveillance.canGetStatistics(this.userId, m)) {
          continue
        }

        for (let j = 0; j !== mc.dset.length; ++j) {
          const d = mc.dset[j]
          if (d.step < 1) {
            continue
          }

          const lastSave = moment(d.lastStepCompleteDate)
          if (!d.name.toLowerCase().includes('spiadi')) {
            const existing = lastUpdateByName[d.name]
            if (existing == null || lastSave.isAfter(existing)) {
              lastUpdateByName[d.name] = lastSave
            }
          } else if (d.name === spiadiNameDset &&
                     (result.spiadi.lastSave == null ||
                      lastSave.isAfter(result.spiadi.lastSave))) {
            result.spiadi.lastSave = lastSave
          }
        }
      }

      for (let i in lastUpdateByName) {
        result.others.push(
          {
            name: i,
            lastSave: lastUpdateByName[i]
          }
        )
      }

      return result
    },

    spiadi: function () {
      const y = parseInt(this.year)
      return (y === 2020 ||
              y === 2021 ||
              y === 2022 ||
              y === 2023 ||
              y === 2024 ||
              y === 2025 ||
              y === 2026) &&
             this.datasets.spiadi.name === this.dataset
    }
  },

  mounted: function () {
    if (this.surveillance != null && this.dataset != null) {
      this.fill()
    }
  },

  watch: {
    surveillance: function () {
      if (this.dataset != null) {
        this.fill()
      }
    },

    dataset: function () {
      this.fill()
    },

    filteredInfections: {
      handler: function (v) {
        if (v.filter !== this.referenceFilteredInfections.filter ||
             v.list.length !== this.referenceFilteredInfections.length) {
          this.referenceFilteredInfections.filter = v.filter
          this.referenceFilteredInfections.list = v.list
        }
      },
      deep: true
    }
  },

  methods: {
    formatDate: function (d) {
      if (d == null || d === '') {
        return null
      }

      const date = moment(d)
      if (date.year() === 1) {
        return null
      }
      return date.format('DD/MM/YYYY HH:mm')
    },

    setDataset: function (d) {
      this.dataset = d.name
      this.datasetUpdateDate = d.lastSave != null && d.lastSave.year() > 1 ? d.lastSave : null
    },

    compareResults: function (r, s) {
      if (r.divisor === s.divisor &&
        r.numerator === s.numerator) {
        return true
      }
      return false
    },

    fill: function () {
      assert.notNull(this.dataset)
      if (this.hasAccess) {
        const vm = this
        vm.dsall = null
        vm.results = null
        const infReq = {
          surv: this.surveillance.id,
          year: this.surveillance.year,
          dset: this.dataset
        }
        const dstReq = {
          surv: vm.surveillance.id,
          year: vm.surveillance.year,
          name: this.dataset,
          step: 2
        }

        if (this.isRegistrator) {
          vm.restricted = false
        }

        if (vm.restricted) {
          const mods = []
          for (let m in this.surveillance.modules) {
            if (m !== 'results' &&
            this.surveillance.canGetStatistics(this.userId, m)) {
              mods.push(m)
            }
          }
          infReq.mods = mods
          dstReq.mods = mods
        }

        if (this.spiadi) {
          vm.$store.dispatch(
            'results/loadResult',
            {
              s: this.surveillance.id,
              d: this.dataset
            }
          ).then(
            (response) => {
              if (response.data.err != null &&
                  response.data.err !== '[NoResults]') {
                vm.$notify({
                  type: 'danger',
                  title: 'Erreur !',
                  duration: 0,
                  content: `Une erreur est survenue pendant la récupération des
  incidences : ${response.data.err}.`
                })
              }
              vm.results = this.getResult(this.surveillance.id, this.dataset)
              return response
            }
          ).catch(
            e => {
              vm.$notify({
                type: 'danger',
                title: 'Erreur !',
                duration: 0,
                content: `Une erreur est survenue pendant la récupération des
  incidences : ${e}.`
              })
            }
          )
        }

        if (this.isRegistrator || this.hasModule) {
          const list = [
            'getBact',
            'getPneu'
          ]

          list.forEach(
            l => {
              vm.$store.dispatch(`infections/${l}`, infReq).then(
                (response) => {
                  if (response.data.err != null) {
                    vm.$notify({
                      type: 'danger',
                      title: 'Erreur !',
                      duration: 0,
                      content: `Une erreur est survenue pendant la récupération des
    infections : ${response.data.err}.`
                    })
                  }
                  return response
                }
              ).catch(
                e => {
                  vm.$notify({
                    type: 'danger',
                    title: 'Erreur !',
                    duration: 0,
                    content: `Une erreur est survenue pendant la récupération des
    infections : ${e}.`
                  })
                }
              )
            }
          )

          vm.$store.dispatch(
            'surveillances/getCompleteDataset',
            dstReq
          ).then(
            (response) => {
              if (response.data.err != null) {
                vm.$notify({
                  type: 'danger',
                  title: 'Erreur !',
                  duration: 0,
                  content: `Une erreur est survenue pendant la récupération des
    dénominateurs : ${response.data.err}.`
                })
                return response
              }
              vm.dsall = ice.freeze(response.data.den)
              return response
            }
          ).catch(
            e => {
              vm.$notify({
                type: 'danger',
                title: 'Erreur !',
                duration: 0,
                content: `Une erreur est survenue pendant la récupération des
    dénominateurs : ${e}.`
              })
            }
          )
        }
      }
    }
  },

  components: {
    'loading-text': loading,
    'sui-results-dashboard': suiResultsDashboard,
    'sui-results-caracteristics': suiResultsCaracteristics,
    'sui-data-filter-input': suiDataFilterInput,
    'how-to-read-results': readResults
  }
}
</script>
<style scoped lang="less">
#establishments-data {
  margin-bottom: 2rem;

  .help-button {
    margin-top: -2.5rem;
    float: left;
  }

  .tabs {
    padding-top: 0rem;
  }

  .panel-title {
    h4 {
      margin-top: 0rem;
      margin-bottom: 0rem;
    }
  }

  .panel {
    margin-bottom: 0rem;
  }

  .label {
    margin-left: 0.2rem;
    margin-right: 0.2rem;
  }

  .open-button-container {
    padding: 1rem;
  }

  .open-button {
    word-wrap: break-word;
    white-space: normal;
    height: 5rem;
  }
}
</style>
